<script setup>
// A configuração da ferramenta "Atualizar o funil" (CRM-03): o diálogo que abre do card dela em
// Ferramentas, no molde do "Pedir atendimento humano". Aqui o cliente escolhe o funil, marca as
// etapas que o agente alcança, escreve o gatilho de cada uma e diz se o agente grava título e
// valor do negócio no card.
//
// O que NÃO mora aqui é o interruptor (`kanban.ativa`). Ligar e desligar é do card, e só a
// plataforma mexe nele: o backend recusa a quem não é super-admin mudar o valor EFETIVO — e
// aqui ausente é DESLIGADO. Este save manda o bloco com o `ativa` que já estava gravado, por
// spread; sem ele gravado, a chave continua ausente.
//
// As etapas vêm da API do Kanban (a mesma do board): a config guarda só `stage_id`, e o nome,
// a posição e as ações automáticas (`on_enter`) de cada etapa são do funil, não do agente.
import { ref, reactive, computed, inject, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { mensagemDoErro } from '../erros';
import {
  limparGatilho,
  gatilhosDoTexto,
  gatilhosValidos,
  gatilhosRepetidos,
  temGatilhoVazio,
  quandoTexto,
} from '../helpers/gatilhos';
import powerAgentsApi from 'dashboard/api/powerAgents';
import kanbanApi from 'dashboard/api/kanban';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  agent: { type: Object, required: true },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();
const ctx = inject('powerAgentContext');
// O diálogo é teleportado para o body e fica FORA do `<fieldset disabled>` do SectionShell.
const somenteLeitura = inject(
  'powerAgentSomenteLeitura',
  computed(() => false)
);

// Os tetos da API: cada `quando` vai na descrição da tool em TODA requisição ao modelo.
const QUANDO_MAX = 1000;
const QUANDO_TOTAL_MAX = 2000;
const GATILHO_MAX = 200;

const bloco = props.agent.config?.kanban || {};
// Só leitura, e do agente e não do formulário: o interruptor é do card.
const ligada = computed(() => props.agent.config?.kanban?.ativa === true);

const saving = ref(false);
const pipelines = ref([]);
const carregando = ref(true);
const falhouAoCarregar = ref(false);
const pipelineId = ref(bloco.pipeline_id ? String(bloco.pipeline_id) : '');
const preencherTitulo = ref(bloco.preencher?.titulo === true);
const preencherValor = ref(bloco.preencher?.valor === true);

// Uma entrada por etapa JÁ vista, marcada ou não, e ela sobrevive à troca de funil: voltar ao
// funil anterior devolve o que estava escrito. Só vai para a API o que é do funil escolhido.
const porEtapa = reactive({});
function novaEtapa(quandoGravado, marcada) {
  const linhas = gatilhosDoTexto(quandoGravado).map(texto => ({ texto }));
  return {
    marcada,
    linhas: linhas.length ? linhas : [{ texto: '' }],
    gravado: quandoGravado || null,
    // A lista como ela abriu, já limpa: é a régua de "ninguém mexeu".
    iniciais: linhas.map(linha => limparGatilho(linha.texto)).filter(Boolean),
  };
}
(bloco.etapas || []).forEach(etapa => {
  porEtapa[etapa.stage_id] = novaEtapa(etapa.quando, true);
});
// Etapa ainda não vista ganha a sua entrada no watch abaixo, e não aqui: criar estado reativo
// durante o render é o que faz o Vue re-renderizar em laço.
const SEM_ESTADO = Object.freeze({
  marcada: false,
  linhas: [],
  gravado: null,
  iniciais: [],
});
function estadoDe(stageId) {
  return porEtapa[stageId] || SEM_ESTADO;
}

const pipelineEscolhido = computed(() =>
  pipelines.value.find(p => String(p.id) === pipelineId.value)
);
const etapasDoFunil = computed(() =>
  [...(pipelineEscolhido.value?.stages || [])].sort(
    (a, b) => a.position - b.position
  )
);

watch(
  etapasDoFunil,
  stages => {
    stages.forEach(stage => {
      if (!porEtapa[stage.id]) porEtapa[stage.id] = novaEtapa(null, false);
    });
  },
  { immediate: true }
);

// Um id guardado que não está na lista (funil apagado, ou a API falhou) continua aparecendo
// como opção, para o select nunca esvaziar em silêncio.
const opcoesDeFunil = computed(() => {
  const faltando =
    pipelineId.value &&
    !pipelines.value.some(p => String(p.id) === pipelineId.value);
  return [
    ...(faltando
      ? [{ id: pipelineId.value, name: `${pipelineId.value} (?)` }]
      : []),
    ...pipelines.value,
  ];
});

function textos(stageId) {
  return estadoDe(stageId).linhas.map(linha => linha.texto);
}

function quandoDa(stageId) {
  const estado = estadoDe(stageId);
  return quandoTexto(gatilhosValidos(textos(stageId)), {
    iniciais: estado.iniciais,
    gravado: estado.gravado,
  });
}

const marcadas = computed(() =>
  etapasDoFunil.value.filter(stage => estadoDe(stage.id).marcada)
);

// As ações automáticas da etapa (`on_enter`) rodam quando o AGENTE move, como rodam para um
// humano (decisão do Murilo, 23/09). Quem marca a etapa precisa saber disso antes de publicar.
function temAcaoAutomatica(stage) {
  const regras = stage.on_enter || {};
  return Boolean(
    regras.conversation_status ||
    (regras.add_labels || []).length ||
    regras.assign_team_id
  );
}

const semGatilho = computed(() =>
  marcadas.value.filter(stage => !quandoDa(stage.id))
);
const quandoTotal = computed(() =>
  marcadas.value.reduce(
    (soma, stage) => soma + (quandoDa(stage.id) || '').length,
    0
  )
);
const etapaLonga = computed(() =>
  marcadas.value.find(stage => (quandoDa(stage.id) || '').length > QUANDO_MAX)
);

function adicionarGatilho(stageId) {
  estadoDe(stageId).linhas.push({ texto: '' });
}

function removerGatilho(stageId, indice) {
  estadoDe(stageId).linhas.splice(indice, 1);
}

async function carregarFunis() {
  try {
    const { data } = await kanbanApi.getPipelines();
    pipelines.value = data?.payload || [];
  } catch (e) {
    falhouAoCarregar.value = true;
  } finally {
    carregando.value = false;
  }
}

// Recusar aqui com o nome da etapa, e não deixar a API devolver um 422 que fala em
// `kanban.etapas` para quem está olhando uma lista de colunas.
function recusaAntesDeSalvar() {
  // Sem a lista de etapas, o save mandaria `etapas: []` e apagaria o que está configurado.
  if (falhouAoCarregar.value) return t('POWER_AGENTS.KANBAN.PIPELINES_ERROR');
  if (semGatilho.value.length) {
    return t('POWER_AGENTS.KANBAN.STAGE_NEEDS_TRIGGER', {
      etapa: semGatilho.value[0].name,
    });
  }
  if (etapaLonga.value) {
    return t('POWER_AGENTS.KANBAN.WHEN_TOO_LONG', {
      etapa: etapaLonga.value.name,
      max: QUANDO_MAX,
    });
  }
  if (quandoTotal.value > QUANDO_TOTAL_MAX) {
    return t('POWER_AGENTS.KANBAN.WHEN_TOTAL_TOO_LONG', {
      total: quandoTotal.value,
      max: QUANDO_TOTAL_MAX,
    });
  }
  return null;
}

async function save() {
  const recusa = recusaAntesDeSalvar();
  if (recusa) {
    useAlert(recusa);
    return;
  }
  // Lido no save e não no mount: a cópia canônica do agente muda a cada seção salva.
  const atual = props.agent.config?.kanban || {};
  const id = parseInt(pipelineId.value, 10);

  saving.value = true;
  try {
    const updated = await powerAgentsApi.update(
      ctx.accountId(),
      ctx.agentId(),
      {
        config: {
          // Sem `ativa`: o spread leva o que está gravado (ou nada), e mudá-lo é do card.
          kanban: {
            ...atual,
            pipeline_id: Number.isInteger(id) ? id : null,
            etapas: marcadas.value.map(stage => ({
              stage_id: stage.id,
              quando: quandoDa(stage.id),
            })),
            preencher: {
              titulo: preencherTitulo.value,
              valor: preencherValor.value,
            },
          },
        },
      }
    );
    ctx.onSectionSaved(updated);
    // Vale na revisão publicada, como toda config: sem isto, quem salva acha que mudou.
    ctx.markPending();
    useAlert(t('POWER_AGENTS.SAVED'));
    emit('saved');
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.SAVE_ERROR')));
  } finally {
    saving.value = false;
  }
}

onMounted(carregarFunis);

// O botão de salvar é do diálogo, que é de quem abre: o formulário não sabe em que moldura está.
defineExpose({ save, saving });
</script>

<template>
  <fieldset
    :disabled="somenteLeitura"
    class="flex flex-col min-w-0 gap-5 p-0 m-0 border-0"
  >
    <!-- O estado da ferramenta, e não um controle: o interruptor mora no card. -->
    <div class="flex flex-wrap items-center gap-2">
      <span
        class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full"
        :class="
          ligada ? 'bg-n-teal-3 text-n-teal-11' : 'bg-n-alpha-2 text-n-slate-11'
        "
      >
        {{
          ligada
            ? t('POWER_AGENTS.KANBAN.STATE_ON')
            : t('POWER_AGENTS.KANBAN.STATE_OFF')
        }}
      </span>
      <span v-if="!ligada" class="text-xs text-n-slate-11">
        {{ t('POWER_AGENTS.KANBAN.OFF_NOTE') }}
      </span>
    </div>

    <div>
      <label class="block mb-1 text-sm font-medium text-n-slate-12">
        {{ t('POWER_AGENTS.KANBAN.PIPELINE') }}
      </label>
      <select
        v-model="pipelineId"
        data-teste="kanban-funil"
        class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 outline-none focus:border-n-brand mb-0"
      >
        <option value="">{{ t('POWER_AGENTS.KANBAN.PIPELINE_NONE') }}</option>
        <option v-for="p in opcoesDeFunil" :key="p.id" :value="String(p.id)">
          {{ p.name }}
        </option>
      </select>
      <p v-if="falhouAoCarregar" class="mt-1 text-xs text-n-ruby-11">
        {{ t('POWER_AGENTS.KANBAN.PIPELINES_ERROR') }}
      </p>
    </div>

    <!-- As etapas do funil escolhido: marcar é pôr ao alcance do agente; cada marcada pede os
         gatilhos dela, em lista, no formato do pedido de humano. Botões com `type="button"`:
         o diálogo é um <form>, e sem tipo eles salvariam. -->
    <section v-if="pipelineEscolhido" class="flex flex-col gap-3">
      <div>
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ t('POWER_AGENTS.KANBAN.STAGES') }}
        </h4>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.KANBAN.STAGES_HINT') }}
        </p>
      </div>

      <div
        v-for="stage in etapasDoFunil"
        :key="stage.id"
        :data-etapa="stage.id"
        class="p-3 border rounded-lg border-n-weak"
      >
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <Checkbox v-model="estadoDe(stage.id).marcada" />
          <span class="font-medium">{{ stage.name }}</span>
          <span
            v-if="stage.behavior !== 'none'"
            class="text-xs text-n-slate-10"
          >
            {{
              stage.behavior === 'won'
                ? t('POWER_AGENTS.KANBAN.STAGE_WON')
                : t('POWER_AGENTS.KANBAN.STAGE_LOST')
            }}
          </span>
        </label>

        <div v-if="estadoDe(stage.id).marcada" class="mt-2">
          <p
            v-if="temAcaoAutomatica(stage)"
            data-teste="kanban-on-enter"
            class="mb-2 text-xs text-n-amber-11"
          >
            {{ t('POWER_AGENTS.KANBAN.ON_ENTER_WARNING') }}
          </p>
          <div class="flex flex-col gap-2">
            <div
              v-for="(linha, indice) in estadoDe(stage.id).linhas"
              :key="indice"
              class="flex items-center gap-2"
            >
              <input
                v-model="linha.texto"
                type="text"
                :maxlength="GATILHO_MAX"
                class="reset-base flex-1 min-w-0 px-2 py-1.5 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand placeholder:text-n-slate-10"
                :placeholder="t('POWER_AGENTS.KANBAN.WHEN_PLACEHOLDER')"
                :aria-label="t('POWER_AGENTS.KANBAN.WHEN_ITEM')"
              />
              <Button
                type="button"
                variant="faded"
                color="slate"
                size="xs"
                icon="i-lucide-trash-2"
                class="shrink-0"
                :aria-label="t('POWER_AGENTS.KANBAN.WHEN_REMOVE')"
                @click="removerGatilho(stage.id, indice)"
              />
            </div>
          </div>
          <p
            v-if="temGatilhoVazio(textos(stage.id))"
            class="mt-2 text-xs text-n-amber-11"
          >
            {{ t('POWER_AGENTS.KANBAN.WHEN_EMPTY_WARNING') }}
          </p>
          <p
            v-if="gatilhosRepetidos(textos(stage.id)).length"
            class="mt-2 text-xs text-n-amber-11"
          >
            {{
              t('POWER_AGENTS.KANBAN.WHEN_DUPLICATE_WARNING', {
                itens: gatilhosRepetidos(textos(stage.id)).join(', '),
              })
            }}
          </p>
          <Button
            type="button"
            variant="faded"
            color="slate"
            size="xs"
            icon="i-lucide-plus"
            class="mt-2"
            :label="t('POWER_AGENTS.KANBAN.WHEN_ADD')"
            @click="adicionarGatilho(stage.id)"
          />
        </div>
      </div>

      <p v-if="quandoTotal > QUANDO_TOTAL_MAX" class="text-xs text-n-ruby-11">
        {{
          t('POWER_AGENTS.KANBAN.WHEN_TOTAL_TOO_LONG', {
            total: quandoTotal,
            max: QUANDO_TOTAL_MAX,
          })
        }}
      </p>
    </section>
    <p v-else-if="!carregando && !pipelineId" class="text-xs text-n-slate-10">
      {{ t('POWER_AGENTS.KANBAN.PIPELINE_PICK') }}
    </p>

    <!-- Título e valor: só se ligados o modelo vê os campos na tool. O agente nunca deduz um
         valor; grava só o que a pessoa disse. -->
    <section class="flex flex-col gap-3">
      <h4 class="text-sm font-semibold text-n-slate-12">
        {{ t('POWER_AGENTS.KANBAN.FILL') }}
      </h4>
      <div class="flex items-start gap-3">
        <Switch v-model="preencherTitulo" class="mt-0.5" />
        <div class="min-w-0">
          <span class="block text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.KANBAN.FILL_TITLE') }}
          </span>
          <span class="block mt-0.5 text-xs text-n-slate-10">
            {{ t('POWER_AGENTS.KANBAN.FILL_TITLE_HINT') }}
          </span>
        </div>
      </div>
      <div class="flex items-start gap-3">
        <Switch v-model="preencherValor" class="mt-0.5" />
        <div class="min-w-0">
          <span class="block text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.KANBAN.FILL_VALUE') }}
          </span>
          <span class="block mt-0.5 text-xs text-n-slate-10">
            {{ t('POWER_AGENTS.KANBAN.FILL_VALUE_HINT') }}
          </span>
        </div>
      </div>
    </section>
  </fieldset>
</template>
